import { apiRequest, ApiResponse } from './api';
import { Contact } from '../types';

export interface ContactFilters {
  tipo?: 'email' | 'fixo' | 'celular';
  is_whatsapp?: boolean;
  recebe_notificacao?: boolean;
  search?: string;
}

export interface ContactListParams {
  page?: number;
  limit?: number;
  filters?: ContactFilters;
}

export interface ContactCreateData {
  cliente_id?: number;
  nome: string;
  tipo: 'email' | 'fixo' | 'celular';
  email?: string;
  numero?: string;
  is_whatsapp: boolean;
  recebe_notificacao: boolean;
  tags?: any[] | null;
}

export interface ContactUpdateData extends Partial<ContactCreateData> {}

class ContactService {
  private baseUrl = '/contacts';

  // Listar todos os contatos
  async getAll(params: ContactListParams = {}): Promise<ApiResponse<Contact[]>> {
    const { page = 1, limit = 10, filters = {} } = params;
    
    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      ...Object.fromEntries(
        Object.entries(filters).map(([key, value]) => [key, String(value)])
      ),
    });

    return apiRequest.get<Contact[]>(`${this.baseUrl}?${queryParams}`);
  }

  // Listar contatos de um cliente
  async getByClientId(
    clientId: number,
    params: ContactListParams = {}
  ): Promise<ApiResponse<Contact[]>> {
    const { page = 1, limit = 10, filters = {} } = params;
    
    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      ...Object.fromEntries(
        Object.entries(filters).map(([key, value]) => [key, String(value)])
      ),
    });

    return apiRequest.get<Contact[]>(`${this.baseUrl}/client/${clientId}?${queryParams}`);
  }

  // Obter contato por ID
  async getById(id: number): Promise<ApiResponse<Contact>> {
    return apiRequest.get<Contact>(`${this.baseUrl}/${id}`);
  }

  // Criar novo contato
  async create(data: ContactCreateData): Promise<Contact> {
    const response = await apiRequest.post<Contact>(this.baseUrl, data);
    return response.data!;
  }

  // Atualizar contato
  async update(id: number, data: ContactUpdateData): Promise<Contact> {
    const response = await apiRequest.put<Contact>(`${this.baseUrl}/${id}`, data);
    return response.data!;
  }

  // Deletar contato
  async delete(id: number): Promise<ApiResponse<void>> {
    return apiRequest.delete<void>(`${this.baseUrl}/${id}`);
  }

  // Alternar notificações
  async toggleNotifications(id: number): Promise<Contact> {
    const contact = await this.getById(id);
    return this.update(id, {
      recebe_notificacao: !contact.data?.recebe_notificacao,
    });
  }

  // Alternar WhatsApp
  async toggleWhatsApp(id: number): Promise<Contact> {
    const contact = await this.getById(id);
    if (contact.data?.tipo !== 'celular') {
      throw new Error('WhatsApp só pode ser habilitado para celular');
    }
    return this.update(id, {
      is_whatsapp: !contact.data?.is_whatsapp,
    });
  }

  // Validar dados do contato antes de enviar
  validateContactData(data: ContactCreateData | ContactUpdateData): string[] {
    const errors: string[] = [];

    // Validar tipo
    if ('tipo' in data && data.tipo) {
      if (!['email', 'fixo', 'celular'].includes(data.tipo)) {
        errors.push('Tipo deve ser: email, fixo ou celular');
      }

      // Validar campo específico baseado no tipo
      if (data.tipo === 'email') {
        if (!data.email || data.email.trim().length === 0) {
          errors.push('Email é obrigatório quando tipo é email');
        } else if (!this.isValidEmail(data.email)) {
          errors.push('Email inválido');
        }
      }

      if (data.tipo === 'fixo' || data.tipo === 'celular') {
        if (!data.numero || data.numero.trim().length === 0) {
          errors.push('Número é obrigatório quando tipo é fixo ou celular');
        } else if (!this.isValidPhone(data.numero)) {
          errors.push('Números deve ter pelo menos 10 dígitos');
        }
      }

      // Validar WhatsApp apenas para celular
      if (data.is_whatsapp && data.tipo !== 'celular') {
        errors.push('WhatsApp só pode ser habilitado para celular');
      }
    }

    return errors;
  }

  // Validar formato de email
  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  // Validar formato de fixo/celular
  private isValidPhone(numero: string): boolean {
    // Remove caracteres não numéricos
    const cleanPhone = numero.replace(/\D/g, '');
    // Aceita telefones com 10 ou 11 dígitos (com ou sem DDD)
    return cleanPhone.length >= 10 && cleanPhone.length <= 11;
  }

  // Formatar dados para exibição
  formatContactForDisplay(contact: Contact) {
    return {
      ...contact,
      tipo_label: this.getTipoLabel(contact.tipo),
      valor_contato: this.getContactValue(contact),
      valor_formatado: this.formatContactValue(contact),
      icone: this.getContactIcon(contact),
    };
  }

  // Obter label do tipo
  private getTipoLabel(tipo: string): string {
    const labels = {
      email: 'Email',
      fixo: 'Fixo',
      celular: 'Celular',
    };
    return labels[tipo as keyof typeof labels] || tipo;
  }

  // Obter valor do contato baseado no tipo
  private getContactValue(contact: Contact): string {
    switch (contact.tipo) {
      case 'email':
        return contact.email || '';
      case 'fixo':
        return contact.numero || '';
      case 'celular':
        return contact.numero || '';
      default:
        return '';
    }
  }

  // Formatar valor do contato para exibição
  private formatContactValue(contact: Contact): string {
    const value = this.getContactValue(contact);
    
    if (contact.tipo === 'fixo' || contact.tipo === 'celular') {
      return this.formatPhone(value);
    }
    
    return value;
  }

  // Formatar número para exibição
  private formatPhone(numero: string): string {
    const cleanPhone = numero.replace(/\D/g, '');
    
    if (cleanPhone.length === 11) {
      // Celular com 11 dígitos: (XX) 9XXXX-XXXX
      return cleanPhone.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    } else if (cleanPhone.length === 10) {
      // Fixo com 10 dígitos: (XX) XXXX-XXXX
      return cleanPhone.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
    }
    
    return numero;
  }

  // Obter ícone do contato
  private getContactIcon(contact: Contact): string {
    switch (contact.tipo) {
      case 'email':
        return 'Email';
      case 'fixo':
        return 'Phone';
      case 'celular':
        return contact.is_whatsapp ? 'WhatsApp' : 'ContactPhone';
      default:
        return 'ContactPhone';
    }
  }

  // Agrupar contatos por tipo
  groupContactsByType(contacts: Contact[]) {
    return contacts.reduce((groups, contact) => {
      const tipo = contact.tipo;
      if (!groups[tipo]) {
        groups[tipo] = [];
      }
      groups[tipo].push(contact);
      return groups;
    }, {} as Record<string, Contact[]>);
  }

  // Obter contato principal (primeiro de cada tipo)
  getPrimaryContacts(contacts: Contact[]) {
    const grouped = this.groupContactsByType(contacts);
    return {
      email: grouped.email?.[0] || null,
      fixo: grouped.fixo?.[0] || null,
      celular: grouped.celular?.[0] || null,
    };
  }

  // Verificar se cliente tem WhatsApp
  hasWhatsApp(contacts: Contact[]): boolean {
    return contacts.some(contact => 
      contact.tipo === 'celular' && contact.is_whatsapp
    );
  }

  // Obter contatos que recebem notificação
  getNotificationContacts(contacts: Contact[]): Contact[] {
    return contacts.filter(contact => contact.recebe_notificacao);
  }
}

export const contactService = new ContactService();
export default contactService; 